import { motion, type Variants } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon, { type IconName } from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { tokenStorage } from "../../config/api";
import { useLogoutMutation } from "../../store/api/authApi";

type UserRole = "admin" | "tenant" | "technician" | "agent";

type AccentColor = "primary" | "secondary" | "accent" | "success";

interface UserType {
	id: UserRole;
	title: string;
	description: string;
	icon: IconName;
	color: AccentColor;
	features: string[];
	route: string;
	gradient: string;
}

const containerVariants: Variants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			duration: 0.6,
			staggerChildren: 0.1,
		},
	},
};

const itemVariants: Variants = {
	hidden: { y: 30, opacity: 0 },
	visible: {
		y: 0,
		opacity: 1,
		transition: {
			duration: 0.5,
			ease: "easeOut",
		},
	},
};

const userTypes: UserType[] = [
	{
		id: "admin",
		title: "Administrator",
		description: "Manage properties, tenants, and all operations",
		icon: "Settings",
		color: "primary",
		features: [
			"Property Management",
			"Tenant Oversight",
			"Payment Tracking",
			"Analytics",
		],
		route: "/login?role=admin",
		gradient: "from-blue-500 to-blue-600",
	},
	{
		id: "tenant",
		title: "Tenant Portal",
		description: "Access your lease, payments, and maintenance",
		icon: "Home",
		color: "secondary",
		features: [
			"Lease Details",
			"Payment History",
			"Maintenance Requests",
			"Communications",
		],
		route: "/login?role=tenant",
		gradient: "from-green-500 to-green-600",
	},
	{
		id: "technician",
		title: "Technician Dashboard",
		description: "Manage maintenance tasks and schedules",
		icon: "Wrench",
		color: "accent",
		features: [
			"Task Management",
			"Schedule Calendar",
			"Work Orders",
			"Communication",
		],
		route: "/login?role=technician",
		gradient: "from-orange-500 to-orange-600",
	},
	{
		id: "agent",
		title: "Sales Agent",
		description: "Handle property sales and client interactions",
		icon: "Users",
		color: "success",
		features: [
			"Sales Management",
			"Client Messaging",
			"Property Listings",
			"Viewing Schedules",
		],
		route: "/login?role=agent",
		gradient: "from-purple-500 to-purple-600",
	},
];

const Home: React.FC = () => {
	const navigate = useNavigate();
	const [isLoaded, setIsLoaded] = useState(false);
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [userRole, setUserRole] = useState<UserRole | null>(null);
	const [logout] = useLogoutMutation();

	useEffect(() => {
		setIsLoaded(true);

		// Check if user is already authenticated
		const authToken = tokenStorage.getAccessToken();
		const storedUserRole = localStorage.getItem("userRole") as UserRole | null;

		if (authToken && storedUserRole) {
			setIsLoggedIn(true);
			setUserRole(storedUserRole);
		}
	}, []);

	const handleSignIn = (route: string) => {
		navigate(route);
	};

	const handleGoToDashboard = () => {
		if (!userRole) return;

		switch (userRole) {
			case "admin":
				navigate("/admin-dashboard");
				break;
			case "tenant":
				navigate("/tenant-portal");
				break;
			case "technician":
				navigate("/technician-dashboard");
				break;
			case "agent":
				navigate("/sales-management");
				break;
			default:
				navigate("/admin-dashboard");
		}
	};

	const handleLogout = async () => {
		try {
			await logout().unwrap();
			tokenStorage.clearTokens();
			setIsLoggedIn(false);
			setUserRole(null);
			navigate("/login");
		} catch (error) {
			console.error("Logout failed:", error);
			// Clear tokens anyway and redirect
			tokenStorage.clearTokens();
			setIsLoggedIn(false);
			setUserRole(null);
			navigate("/login");
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
			{/* Background Pattern */}
			<div className="absolute inset-0 opacity-5">
				<div
					className="absolute inset-0"
					style={{
						backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
					}}
				/>
			</div>
			{/* Hero Section */}
			<motion.div
				className="relative z-10 container mx-auto px-4 py-12"
				variants={containerVariants}
				initial="hidden"
				animate={isLoaded ? "visible" : "hidden"}
			>
				{/* Header */}
				<motion.div className="text-center mb-16" variants={itemVariants}>
					<div className="flex items-center justify-center mb-8">
						<div className="w-16 h-16 bg-primary text-primary-foreground rounded-2xl flex items-center justify-center mr-4 shadow-lg">
							<Icon name="Home" size={32} />
						</div>
						<div className="text-left">
							<h1 className="text-4xl md:text-5xl font-bold text-foreground">
								Dofarahub
							</h1>
						</div>
					</div>

					{/* Logged In Section */}
					{isLoggedIn && (
						<motion.div
							className="bg-card border border-border rounded-2xl p-6 max-w-md mx-auto mb-8"
							variants={itemVariants}
						>
							<h2 className="text-2xl font-bold text-foreground mb-2">
								Welcome Back!
							</h2>
							<p className="text-muted-foreground mb-6">
								You are logged in as{" "}
								{userRole
									? userRole.charAt(0).toUpperCase() + userRole.slice(1)
									: "User"}
							</p>
							<div className="flex flex-col sm:flex-row gap-4 justify-center">
								<Button
									variant="default"
									onClick={handleGoToDashboard}
									className="flex items-center justify-center"
								>
									<Icon name="LayoutDashboard" size={16} className="mr-2" />
									Go to Dashboard
								</Button>
								<Button
									variant="outline"
									onClick={handleLogout}
									className="flex items-center justify-center"
								>
									<Icon name="LogOut" size={16} className="mr-2" />
									Logout
								</Button>
							</div>
						</motion.div>
					)}

					<motion.h2
						className="text-3xl md:text-4xl font-bold text-foreground mb-4"
						variants={itemVariants}
					>
						Welcome to Your Property Management Hub
					</motion.h2>

					<motion.p
						className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8"
						variants={itemVariants}
					>
						Streamline your real estate operations with our comprehensive
						platform. Choose your role below to access your personalized
						dashboard.
					</motion.p>

					{/* Quick Stats */}
					<motion.div
						className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12"
						variants={itemVariants}
					>
						<div className="bg-card border border-border rounded-lg p-4 text-center">
							<h3 className="text-2xl font-bold text-primary">24/7</h3>
							<p className="text-sm text-muted-foreground">Platform Access</p>
						</div>
						<div className="bg-card border border-border rounded-lg p-4 text-center">
							<h3 className="text-2xl font-bold text-success">100%</h3>
							<p className="text-sm text-muted-foreground">Secure Login</p>
						</div>
						<div className="bg-card border border-border rounded-lg p-4 text-center">
							<h3 className="text-2xl font-bold text-accent">4</h3>
							<p className="text-sm text-muted-foreground">User Roles</p>
						</div>
						<div className="bg-card border border-border rounded-lg p-4 text-center">
							<h3 className="text-2xl font-bold text-secondary">Real-time</h3>
							<p className="text-sm text-muted-foreground">Updates</p>
						</div>
					</motion.div>
				</motion.div>

				{/* User Type Cards */}
				<motion.div
					className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 max-w-7xl mx-auto"
					variants={containerVariants}
				>
					{userTypes?.map((userType, index) => (
						<motion.div
							key={userType?.id}
							className="group relative bg-card border border-border rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden"
							variants={itemVariants}
							whileHover={{
								scale: 1.02,
								transition: { duration: 0.2 },
							}}
							onClick={() => handleSignIn(userType?.route)}
						>
							{/* Gradient Background */}
							<div
								className={`absolute inset-0 bg-gradient-to-br ${userType?.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}
							/>

							{/* Content */}
							<div className="relative z-10">
								{/* Icon */}
								<div
									className={`w-12 h-12 bg-${userType?.color}/10 text-${userType?.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
								>
									<Icon name={userType?.icon} size={24} />
								</div>

								{/* Title & Description */}
								<h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">
									{userType?.title}
								</h3>
								<p className="text-muted-foreground text-sm mb-4 leading-relaxed">
									{userType?.description}
								</p>

								{/* Features */}
								<div className="space-y-2 mb-6">
									{userType?.features?.map((feature, idx) => (
										<div
											key={idx}
											className="flex items-center text-xs text-muted-foreground"
										>
											<div
												className={`w-1.5 h-1.5 bg-${userType?.color} rounded-full mr-2`}
											/>
											{feature}
										</div>
									))}
								</div>

								{/* Sign In Button */}
								<Button
									variant="outline"
									size="sm"
									className="w-full group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-all duration-300"
								>
									<Icon name="LogIn" size={16} className="mr-2" />
									Sign In
								</Button>
							</div>

							{/* Hover Effect Border */}
							<div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-primary/20 transition-all duration-300" />
						</motion.div>
					))}
				</motion.div>

				{/* Additional Sign In Options */}
				<motion.div className="mt-16 text-center" variants={itemVariants}>
					<div className="bg-card border border-border rounded-2xl p-8 max-w-2xl mx-auto">
						<h3 className="text-xl font-semibold text-foreground mb-4">
							New to Dofarahub?
						</h3>
						<p className="text-muted-foreground mb-6">
							Create your account and get started with our comprehensive
							property management solution.
						</p>
						<div className="flex flex-col sm:flex-row gap-4 justify-center">
							<Button
								variant="default"
								onClick={() => navigate("/register")}
								className="flex items-center justify-center"
							>
								<Icon name="UserPlus" size={16} className="mr-2" />
								Create Account
							</Button>
							<Button
								variant="outline"
								onClick={() => navigate("/login")}
								className="flex items-center justify-center"
							>
								<Icon name="LogIn" size={16} className="mr-2" />
								General Sign In
							</Button>
						</div>
					</div>
				</motion.div>

				{/* Footer */}
				<motion.div className="mt-16 text-center" variants={itemVariants}>
					<div className="border-t border-border pt-8">
						<p className="text-xs text-muted-foreground mb-2">
							Dofarahub v2.1.0 | Secure Property Management Platform
						</p>
						<p className="text-xs text-muted-foreground">
							Last updated: November {new Date()?.getFullYear()} | All rights
							reserved
						</p>
					</div>
				</motion.div>
			</motion.div>
		</div>
	);
};

export default Home;
